import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Search, X, RefreshCw, ArrowUpDown, AlertCircle, Trash2, Loader2, Laugh, Smile, Meh, Frown,
  MessageSquareHeart, TrendingUp, Copy, Check, ChevronDown, Inbox,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AdminLoginAPi, fetchWithToken } from "api/ApiHandler";
import Sidebar1 from "components/Sidebar1";
import DEfulatImg from "../../Assests/Images/DefulatLogo.png";
import { useDialog, DialogFrame } from "../Admindashboard/ui";
import { FONT_HEADING, PAGE_SIZES, formatDate, fromNow, handleAuthError, Pagination, EmptyState } from "../userManagement/shared";

// Ratings the app sends, best → worst
const RATINGS = [
  { key: "Amazing", Icon: Laugh, color: "#16A34A", soft: "#DCFCE7", text: "#15803D" },
  { key: "Good", Icon: Smile, color: "#84CC16", soft: "#ECFCCB", text: "#4D7C0F" },
  { key: "Okay", Icon: Meh, color: "#F59E0B", soft: "#FEF3C7", text: "#B45309" },
  { key: "Bad", Icon: Frown, color: "#EA352B", soft: "#FDECEA", text: "#C62828" },
];
const ratingMeta = (r) => RATINGS.find((x) => x.key.toLowerCase() === String(r || "").toLowerCase()) || {
  key: r || "Unrated", Icon: Meh, color: "#9CA3AF", soft: "#F3F4F6", text: "#6B7280",
};

const SORTS = {
  newest: { label: "Newest first", fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  oldest: { label: "Oldest first", fn: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) },
  worst: { label: "Lowest rating first", fn: (a, b) => rank(b.rating) - rank(a.rating) },
  best: { label: "Highest rating first", fn: (a, b) => rank(a.rating) - rank(b.rating) },
};
function rank(r) {
  const i = RATINGS.findIndex((x) => x.key.toLowerCase() === String(r || "").toLowerCase());
  return i === -1 ? RATINGS.length : i;
}

const getName = (u) => `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "Anonymous";
const getAvatar = (u) => {
  const path = u?.imagePath || u?.image;
  return !path || path === "image" ? DEfulatImg : path;
};

export default function Adminallfeedbacks() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [rating, setRating] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("newest");
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1);

  const [confirmItem, setConfirmItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadFeedback = async () => {
    try {
      setIsLoading(true);
      setLoadError("");
      const data = await fetchWithToken("/feedback/getfeedbacks");
      setFeedback(Array.isArray(data?.feedback) ? data.feedback : []);
    } catch (error) {
      console.error("Fetch error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setLoadError("Unable to load feedback.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [rating, searchTerm, sortKey, pageSize]);

  const deleteFeedback = async () => {
    if (!confirmItem) return;
    setDeleting(true);
    try {
      await AdminLoginAPi("/feedback/deleteFeedback", { id: confirmItem._id });
      setFeedback((prev) => prev.filter((item) => item._id !== confirmItem._id));
      toast.success("Feedback deleted.");
      setConfirmItem(null);
    } catch (error) {
      console.error("Delete failed:", error.response?.data || error);
      if (!handleAuthError(error)) toast.error("Couldn’t delete this feedback. Try again.");
    } finally {
      setDeleting(false);
    }
  };

  // ── Stats ──
  const counts = useMemo(() => {
    const c = { All: feedback.length };
    RATINGS.forEach((r) => { c[r.key] = 0; });
    feedback.forEach((f) => {
      const k = ratingMeta(f.rating).key;
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [feedback]);

  const positive = (counts.Amazing || 0) + (counts.Good || 0);
  const satisfaction = feedback.length ? Math.round((positive / feedback.length) * 100) : 0;
  const thisWeek = feedback.filter((f) => moment.utc(f.createdAt).isAfter(moment().subtract(7, "days"))).length;

  const monthly = useMemo(() => {
    const months = Array.from({ length: 6 }, (_, i) => moment().subtract(5 - i, "months").startOf("month"));
    return months.map((m) => {
      const row = { name: m.format("MMM") };
      RATINGS.forEach((r) => { row[r.key] = 0; });
      feedback.forEach((f) => {
        if (moment.utc(f.createdAt).isSame(m, "month")) {
          const k = ratingMeta(f.rating).key;
          if (k in row) row[k] += 1;
        }
      });
      return row;
    });
  }, [feedback]);

  // ── Filtering ──
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return feedback
      .filter((f) => rating === "All" || ratingMeta(f.rating).key === rating)
      .filter((f) =>
        !term ||
        (f.feedback || "").toLowerCase().includes(term) ||
        getName(f.userId).toLowerCase().includes(term) ||
        (f.userId?.email || "").toLowerCase().includes(term)
      )
      .sort(SORTS[sortKey].fn);
  }, [feedback, rating, searchTerm, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const currentItems = filtered.slice(pageStart, pageStart + pageSize);

  return (
    <>
      <Helmet>
        <title>Fuelingo | Feedback</title>
        <meta name="description" content="Feedback from Fuelingo app users, from Amazing to Bad." />
      </Helmet>
      <div className="flex h-screen bg-[#F6F6F8] overflow-hidden">
        <ToastContainer position="top-right" autoClose={2500} hideProgressBar />
        <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

        <main className="flex-1 min-w-0 overflow-y-auto px-8 py-6 lg:px-6 md:px-4 sm:px-3">
          {/* Header */}
          <header className="flex items-center justify-between gap-4 mb-6 sm:flex-col sm:items-start">
            <div>
              <h1 className="m-0 text-3xl sm:text-2xl font-medium text-[#111]" style={FONT_HEADING}>Feedback</h1>
              <p className="m-0 mt-1 text-sm text-[#6B7280]">What users say about the Fuelingo app — sorted by how they rated their experience.</p>
            </div>
            <button
              type="button"
              onClick={loadFeedback}
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-[#fff] border border-[#ECECEF] px-4 py-2.5 text-sm font-medium text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B] transition disabled:opacity-60"
            >
              <RefreshCw size={15} className={isLoading ? "animate-spin" : ""} /> Refresh
            </button>
          </header>

          {/* Overview */}
          <section className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:grid-cols-1 gap-6 md:gap-4 mb-6">
            {/* Satisfaction */}
            <div
              className="relative overflow-hidden rounded-3xl p-6 sm:p-5 text-[#fff]"
              style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 55%, #EA352B 135%)" }}
            >
              <div className="pointer-events-none absolute -right-14 -top-20 w-64 h-64 rounded-full bg-[#EA352B] opacity-20" />
              <div className="relative">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="m-0 text-xs uppercase tracking-wider text-[#ffffff99]">Satisfaction</p>
                    {isLoading ? (
                      <div className="h-12 w-28 mt-2 rounded-lg bg-[#ffffff1f] animate-pulse" />
                    ) : (
                      <p className="m-0 mt-1 text-5xl font-semibold leading-none">{satisfaction}<span className="text-2xl text-[#ffffffb3]">%</span></p>
                    )}
                    <p className="m-0 mt-2 text-xs text-[#ffffffb3]">rated Amazing or Good · {feedback.length} responses · {thisWeek} this week</p>
                  </div>
                  <span className="w-12 h-12 rounded-2xl bg-[#ffffff1f] flex items-center justify-center shrink-0">
                    <MessageSquareHeart size={22} />
                  </span>
                </div>

                {/* Distribution bar */}
                <div className="h-3 rounded-full bg-[#ffffff1a] overflow-hidden flex mt-5">
                  {!isLoading && RATINGS.map((r) => (
                    <div
                      key={r.key}
                      title={`${r.key}: ${counts[r.key]}`}
                      className="h-full transition-all duration-700"
                      style={{ width: `${feedback.length ? (counts[r.key] / feedback.length) * 100 : 0}%`, background: r.color }}
                    />
                  ))}
                </div>

                <div className="grid grid-cols-4 sm:grid-cols-2 gap-2 mt-4">
                  {RATINGS.map((r) => {
                    const active = rating === r.key;
                    const pct = feedback.length ? Math.round((counts[r.key] / feedback.length) * 100) : 0;
                    return (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => setRating(active ? "All" : r.key)}
                        aria-pressed={active}
                        className={`text-left rounded-2xl px-3 py-2.5 border transition ${
                          active ? "bg-[#fff] border-[#fff] text-[#111]" : "bg-[#ffffff14] border-[#ffffff1f] hover:bg-[#ffffff26]"
                        }`}
                      >
                        <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: active ? r.text : "#fff" }}>
                          <r.Icon size={15} style={{ color: r.color }} /> {r.key}
                        </span>
                        <span className="block mt-1 text-lg font-semibold">{isLoading ? "…" : counts[r.key]}</span>
                        <span className={`block text-[10px] ${active ? "text-[#6B7280]" : "text-[#ffffff99]"}`}>{pct}%</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Trend */}
            <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 sm:p-5" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-[#FDECEA] flex items-center justify-center"><TrendingUp size={15} className="text-[#EA352B]" /></span>
                  <h3 className="m-0 text-base font-medium text-[#111]" style={FONT_HEADING}>Last 6 months</h3>
                </div>
                <div className="flex gap-3 flex-wrap text-[11px] text-[#6B7280]">
                  {RATINGS.map((r) => (
                    <span key={r.key} className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: r.color }} />{r.key}</span>
                  ))}
                </div>
              </div>
              <div className="h-[220px] mt-4">
                {isLoading ? (
                  <div className="h-full rounded-2xl bg-[#F3F3F6] animate-pulse" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthly} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#EEEEF1" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 12 }} />
                      <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 12 }} />
                      <Tooltip
                        cursor={{ fill: "#F7F7F9" }}
                        content={({ active, payload, label }) =>
                          active && payload?.length ? (
                            <div className="rounded-xl bg-[#1B1B22] text-[#fff] px-3 py-2 text-xs shadow-lg">
                              <p className="m-0 mb-1 text-[#ffffff99]">{label}</p>
                              {[...payload].reverse().map((p) => (
                                <p key={p.dataKey} className="m-0 flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full" style={{ background: p.fill }} /> {p.dataKey}: <b>{p.value}</b>
                                </p>
                              ))}
                            </div>
                          ) : null
                        }
                      />
                      {[...RATINGS].reverse().map((r, i) => (
                        <Bar key={r.key} dataKey={r.key} stackId="a" fill={r.color} maxBarSize={34} radius={i === RATINGS.length - 1 ? [6, 6, 0, 0] : 0} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </section>

          {/* List */}
          <section className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 md:p-5 sm:p-4" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <div className="relative flex-1 min-w-[220px] sm:min-w-0 sm:w-full">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
                {/* No `type` attr: styles/tailwind.css resets padding/border on typed inputs */}
                <input
                  role="searchbox"
                  enterKeyHint="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
                  placeholder="Search feedback or user"
                  className="w-full pl-10 pr-9 py-2.5 bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl text-sm text-[#111] placeholder-[#9CA3AF] focus:outline-none focus:border-[#EA352B] focus:bg-[#fff] focus:ring-0 transition"
                />
                {searchTerm && (
                  <button type="button" aria-label="Clear search" onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111]">
                    <X size={15} />
                  </button>
                )}
              </div>
              <label className="relative inline-flex items-center sm:flex-1">
                <ArrowUpDown size={14} className="absolute left-3 text-[#9CA3AF] pointer-events-none" />
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  aria-label="Sort feedback"
                  className="w-full appearance-none bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl pl-8 pr-8 py-2.5 text-sm text-[#374151] focus:outline-none focus:border-[#EA352B] focus:ring-0 cursor-pointer"
                >
                  {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                </select>
              </label>
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-5">
              {["All", ...RATINGS.map((r) => r.key)].map((k) => {
                const m = k === "All" ? null : ratingMeta(k);
                const active = rating === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setRating(k)}
                    className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                      active ? "bg-[#EA352B] text-[#fff] shadow-sm" : "bg-[#F3F3F6] text-[#6B7280] hover:text-[#111]"
                    }`}
                  >
                    {m && <m.Icon size={13} style={{ color: active ? "#fff" : m.color }} />}
                    {k}
                    <span className={`rounded-full px-1.5 py-px text-[10px] ${active ? "bg-[#ffffff33]" : "bg-[#fff]"}`}>{isLoading ? "…" : counts[k] || 0}</span>
                  </button>
                );
              })}
              {(rating !== "All" || searchTerm) && (
                <button type="button" onClick={() => { setRating("All"); setSearchTerm(""); }} className="ml-auto text-xs font-semibold text-[#EA352B] hover:text-[#C92A21]">
                  Clear filters
                </button>
              )}
            </div>

            {loadError ? (
              <EmptyState
                Icon={AlertCircle}
                title={loadError}
                text="Check your connection and try again."
                action={<button type="button" onClick={loadFeedback} className="mt-3 rounded-xl bg-[#EA352B] text-[#fff] px-4 py-2 text-sm font-medium">Try again</button>}
              />
            ) : isLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
                {Array.from({ length: 4 }).map((_, i) => <FeedbackSkeleton key={i} />)}
              </div>
            ) : currentItems.length === 0 ? (
              <EmptyState
                Icon={Inbox}
                title="No feedback found"
                text={rating !== "All" || searchTerm ? "Try a different rating or search." : "Feedback from the app will show up here."}
              />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
                {currentItems.map((f) => <FeedbackCard key={f._id} item={f} onDelete={() => setConfirmItem(f)} />)}
              </div>
            )}

            {!isLoading && !loadError && filtered.length > 0 && (
              <div className="flex items-center justify-between gap-3 flex-wrap mt-6 pt-4 border-t border-[#F1F1F4]">
                <div className="flex items-center gap-3 text-xs text-[#6B7280]">
                  <span>
                    Showing <b className="text-[#111]">{pageStart + 1}–{Math.min(pageStart + pageSize, filtered.length)}</b> of <b className="text-[#111]">{filtered.length}</b>
                  </span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    aria-label="Items per page"
                    className="bg-[#F7F7F9] border border-[#EEEEF1] rounded-lg py-1 pl-2 pr-7 text-xs focus:outline-none focus:ring-0 focus:border-[#EA352B]"
                  >
                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                </div>
                {totalPages > 1 && <Pagination page={currentPage} total={totalPages} onChange={setCurrentPage} />}
              </div>
            )}
          </section>
        </main>

        <ConfirmDelete item={confirmItem} busy={deleting} onCancel={() => setConfirmItem(null)} onConfirm={deleteFeedback} />
      </div>
    </>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

function RatingBadge({ rating }) {
  const m = ratingMeta(rating);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap" style={{ background: m.soft, color: m.text }}>
      <m.Icon size={13} style={{ color: m.color }} /> {m.key}
    </span>
  );
}

function FeedbackCard({ item, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const m = ratingMeta(item.rating);
  const text = item.feedback || "";
  const long = text.length > 220;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <article className="group relative rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] hover:bg-[#fff] hover:shadow-[0_8px_24px_rgba(17,17,26,0.06)] transition-all overflow-hidden flex flex-col">
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: m.color }} />
      <div className="p-4 pl-5 flex-1">
        <div className="flex items-center gap-3">
          <img src={getAvatar(item.userId)} onError={(e) => { e.currentTarget.src = DEfulatImg; }} alt="" className="w-10 h-10 rounded-xl object-cover bg-[#F3F3F6] shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="m-0 text-sm font-medium text-[#111] truncate">{getName(item.userId)}</p>
            <p className="m-0 text-[11px] text-[#9CA3AF] truncate" title={formatDate(item.createdAt, true)}>
              {item.userId?.email ? `${item.userId.email} · ` : ""}{fromNow(item.createdAt)}
            </p>
          </div>
          <RatingBadge rating={item.rating} />
        </div>

        <p
          className="m-0 mt-3 text-[13.5px] text-[#374151] leading-relaxed whitespace-pre-line break-words"
          style={!expanded && long ? { display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" } : undefined}
        >
          {text ? <>&ldquo;{text}&rdquo;</> : <span className="text-[#9CA3AF] italic">No written feedback — rating only.</span>}
        </p>
        {long && (
          <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#EA352B]">
            {expanded ? "Show less" : "Read more"} <ChevronDown size={13} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-4 pl-5 py-2 border-t border-[#EEEEF1]">
        <span className="text-[11px] text-[#9CA3AF]">{formatDate(item.createdAt, true)}</span>
        <div className="flex gap-1">
          {text && (
            <button type="button" onClick={copy} title="Copy text" aria-label="Copy feedback text" className="w-8 h-8 rounded-lg flex items-center justify-center text-[#6B7280] hover:bg-[#F3F3F6] transition">
              {copied ? <Check size={15} className="text-[#16A34A]" /> : <Copy size={15} />}
            </button>
          )}
          <button type="button" onClick={onDelete} title="Delete" aria-label="Delete feedback" className="w-8 h-8 rounded-lg flex items-center justify-center text-[#EA352B] hover:bg-[#FDECEA] transition">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </article>
  );
}

function FeedbackSkeleton() {
  return (
    <div className="rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#F1F1F4] animate-pulse" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-28 bg-[#F1F1F4] rounded animate-pulse" />
          <div className="h-2 w-20 bg-[#F1F1F4] rounded animate-pulse" />
        </div>
        <div className="h-6 w-16 bg-[#F1F1F4] rounded-full animate-pulse" />
      </div>
      <div className="h-3 w-full bg-[#F1F1F4] rounded animate-pulse" />
      <div className="h-3 w-3/4 bg-[#F1F1F4] rounded animate-pulse" />
    </div>
  );
}

function ConfirmDelete({ item, busy, onCancel, onConfirm }) {
  useDialog(!!item && !busy, onCancel);
  if (!item) return null;
  return (
    <DialogFrame labelledBy="fb-confirm-title" onClose={busy ? () => {} : onCancel} maxWidth={420}>
      <div className="p-6 bg-[#fff]">
        <span className="w-14 h-14 rounded-2xl bg-[#FDECEA] flex items-center justify-center">
          <Trash2 size={24} className="text-[#EA352B]" />
        </span>
        <h3 id="fb-confirm-title" className="m-0 mt-4 text-lg font-medium text-[#111]" style={FONT_HEADING}>Delete this feedback?</h3>
        <p className="m-0 mt-2 text-sm text-[#6B7280] leading-relaxed">
          Feedback from <b className="text-[#111]">{getName(item.userId)}</b> will be removed permanently. This can’t be undone.
        </p>
        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onCancel} disabled={busy} className="flex-1 rounded-xl border border-[#EEEEF1] py-2.5 text-sm font-semibold text-[#374151] hover:bg-[#F7F7F9] transition disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#EA352B] py-2.5 text-sm font-semibold text-[#fff] hover:bg-[#D42F26] transition disabled:opacity-70">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete
          </button>
        </div>
      </div>
    </DialogFrame>
  );
}
